import React, { useEffect, useMemo, useRef, useState } from 'react';
import DatePickerLib, { registerLocale } from 'react-datepicker';
import ptBr from 'date-fns/locale/pt';
import moment from 'moment';

import 'react-datepicker/dist/react-datepicker.css';

import {
  CalendarButton,
  CalendarPopover,
  DateInputShape,
  DateInputSize,
  FieldWrap,
  IconSlot,
  Label,
  StyledInput,
  Wrapper,
} from './styles';

registerLocale('ptBr', ptBr);

export type DateInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type' | 'value' | 'onChange'
> & {
  label?: string;
  size?: DateInputSize;
  shape?: DateInputShape;
  fullWidth?: boolean;
  color?: string;
  borderColor?: string;
  backgroundColor?: string;
  icon?: React.ReactNode;
  withPicker?: boolean;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const DefaultCalendarIcon = (): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M5 1.5V3M11 1.5V3M2.5 6.5H13.5M3.5 2.5H12.5C13.0523 2.5 13.5 2.94772 13.5 3.5V13.5C13.5 14.0523 13.0523 14.5 12.5 14.5H3.5C2.94772 14.5 2.5 14.0523 2.5 13.5V3.5C2.5 2.94772 2.94772 2.5 3.5 2.5Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const parseDateValue = (value?: string): Date | null => {
  if (!value) return null;

  const parsed = moment(value, 'DD/MM/YYYY', true);

  return parsed.isValid() ? parsed.toDate() : null;
};

const DateInput = ({
  label,
  size = 'md',
  shape = 'pill',
  fullWidth = false,
  color,
  borderColor,
  backgroundColor,
  icon,
  id,
  placeholder = '__/__/____',
  withPicker = true,
  value = '',
  onChange,
  disabled,
  ...rest
}: DateInputProps): JSX.Element => {
  const inputId =
    id || (label ? label.replace(/\s/g, '-').toLowerCase() : undefined);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const selectedDate = useMemo(() => parseDateValue(value), [value]);

  const inputProps = {
    $size: size,
    $shape: shape,
    $fullWidth: fullWidth,
    $color: color,
    $borderColor: borderColor,
    $backgroundColor: backgroundColor,
  };

  const calendarIcon = icon || <DefaultCalendarIcon />;

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (!fieldRef.current?.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);

  const handleCalendarSelect = (date: Date | null) => {
    const formatted = date ? moment(date).format('DD/MM/YYYY') : '';

    onChange?.({
      target: { value: formatted },
    } as React.ChangeEvent<HTMLInputElement>);
    setIsCalendarOpen(false);
  };

  return (
    <Wrapper $fullWidth={fullWidth}>
      {label && (
        <Label htmlFor={inputId} $color={color}>
          {label}
        </Label>
      )}
      <FieldWrap ref={fieldRef}>
        <StyledInput
          id={inputId}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          {...inputProps}
          {...rest}
        />

        {withPicker ? (
          <>
            <CalendarButton
              type="button"
              aria-label="Abrir calendário"
              aria-expanded={isCalendarOpen}
              disabled={disabled}
              onClick={() => setIsCalendarOpen((current) => !current)}
            >
              {calendarIcon}
            </CalendarButton>
            {isCalendarOpen && (
              <CalendarPopover>
                <DatePickerLib
                  inline
                  selected={selectedDate}
                  onChange={handleCalendarSelect}
                  locale="ptBr"
                  disabled={disabled}
                />
              </CalendarPopover>
            )}
          </>
        ) : (
          <IconSlot aria-hidden="true">{calendarIcon}</IconSlot>
        )}
      </FieldWrap>
    </Wrapper>
  );
};

export default DateInput;
